import type { Text } from '@kkf/design'
import { appUrl } from '@kkf/design'
import { LINKS } from './links'

/**
 * Content for the Garage — the community garage and vocational training facility —
 * drawn from `docs/resources/Inquiry letter v2` and the case for support, which names it
 * and describes it mentoring young people "as they serve off-road customers, honoring
 * the dignity of all work".
 *
 * The UNICEF finding is quoted from that letter and is attributed on the page.
 *
 * This was one page on the Foundation site. It is four pages on its own domain now, so
 * the content below is grouped by the page it belongs to: home, services, programmes,
 * contact. Anything pointing at a sibling app carries an absolute URL from `LINKS`.
 */

export const GARAGE_INTRO = {
  banner: '/assets/img/garage-riders-camp.jpg',
  image: '/assets/img/garage-trail-training.jpg',
  imageAlt: { en: 'Two riders in motocross gear starting an off-road bike on a grass trail', mn: 'Мотокроссын хувцастай хоёр унаач өвсөн замд замын бус мотоцикл асааж байна' },
  subTitle: { en: 'Vocational training & mentoring', mn: 'Мэргэжлийн сургалт ба зөвлөгөө' },
  title: { en: 'A Garage That Builds People As Well As Vehicles', mn: 'Тээврийн хэрэгслийн зэрэгцээ хүнийг бүтээдэг гараж' },
  paragraphs: [
    { en: 'King of Kings Garage is a working workshop and a training space. Young people learn hands-on mechanical skills as they serve real off-road customers, taught by mentors who take the work seriously — because all honest work carries dignity, and a trade is something no one can take away from you.', mn: 'King of Kings Garage бол ажиллаж буй засварын газар бөгөөд сургалтын орон зай юм. Залуучууд жинхэнэ замын бус үйлчлүүлэгчдэд үйлчлэхийн зэрэгцээ механикийн ур чадварыг гардан сурдаг бөгөөд ажлыг нухацтай авч үздэг зөвлөхүүд заадаг — учир нь шударга хөдөлмөр бүр нэр төртэй бөгөөд мэргэжил бол хэн ч чамаас булаан авч чадахгүй зүйл юм.' },
    { en: 'It is also a safe community centre. Teenagers are accepted here, encouraged, and guided by adults who keep turning up. Through those relationships they gain practical skills and something harder to teach: identity, dignity and purpose in Christ.', mn: 'Энэ нь бас аюулгүй олон нийтийн төв юм. Өсвөр үеийнхнийг энд хүлээн авч, урамшуулж, байнга ирдэг насанд хүрэгчид чиглүүлдэг. Тэр харилцаагаар дамжуулан тэд практик ур чадвар, мөн заахад илүү хэцүү зүйлийг олж авдаг: Христ доторх өөрийн мөн чанар, нэр төр, зорилго.' },
  ],
}

export const GARAGE_NEED = {
  subTitle: { en: 'Why it matters', mn: 'Яагаад энэ чухал вэ' },
  title: { en: 'The Trajectory We Are Trying To Change', mn: 'Бидний өөрчлөхийг оролдож буй зам' },
  paragraphs: [
    { en: 'Many of the young people we serve come from broken families. Without positive role models, practical opportunities or a supportive community, they face a far greater risk of addiction, homelessness, violence, disengagement from school and long-term unemployment.', mn: 'Бидний үйлчилдэг олон залуу бутарсан гэр бүлээс гаралтай. Сайн үлгэр дуурайл, практик боломж, дэмжих хамт олонгүй бол тэд донтолт, орон гэргүйдэл, хүчирхийлэл, сургуулиас хөндийрөх, урт хугацааны ажилгүйдлийн эрсдэлд илүү өртдөг.' },
    { en: 'We believe early intervention — mentoring, vocational training and meaningful relationships — can change that trajectory.', mn: 'Эрт оролцоо — зөвлөгөө, мэргэжлийн сургалт, утга учиртай харилцаа — тэр замыг өөрчилж чадна гэдэгт бид итгэдэг.' },
  ],
  stat: {
    figure: 'Five-fold',
    txt: 'increase since 2013 in the proportion of adolescent deaths due to suicide among 10–14-year-olds in Mongolia',
    source: 'UNICEF, reporting rising substance abuse and mental health problems among Mongolian adolescents.',
  },
}

/**
 * The paid side of the workshop. This is what a customer off the trail is buying, and it
 * is what pays for the programmes below.
 */
export const GARAGE_SERVICES_HEADING = {
  subTitle: { en: 'What we do for customers', mn: 'Бид үйлчлүүлэгчдэд юу хийдэг вэ' },
  title: { en: 'Recovery, Repair and Trail Support', mn: 'Чирэх, засвар, замын дэмжлэг' },
  descr:
    { en: 'The workshop sits on one of Mongolia’s great off-road routes. Everything on this list is a real service, sold at a real price — and the margin on it is what keeps the youth programmes free.', mn: 'Засварын газар Монголын нэрт замын бус замуудын нэг дээр байрладаг. Энэ жагсаалт дахь бүхэн жинхэнэ үйлчилгээ бөгөөд жинхэнэ үнээр зарагддаг — түүнээс гарах ашиг нь залуучуудын хөтөлбөрийг үнэгүй байлгадаг.' },
}

/**
 * ── A note on the photographs, covering both tables below ─────────────────────────────
 *
 * There are **fourteen cards and six garage photographs**, so several repeat: the
 * workshop lift, the repair bench and the carrier's bare frame each appear twice across
 * services and programmes. Two resort frames stand in where they are genuinely apt — the
 * cabins for "linked with the resort", the reading room for "faith & discipleship" — and
 * one carrier frame stands in for fabrication, which is what it actually shows.
 *
 * None of it is a stock photograph and none of it is of something that is not ours, which
 * is the line that matters. It is still a shortage, and it is recorded on the garage PRD
 * beside the tour and product photography gaps. Fourteen real frames would fix it.
 *
 * `group` exists so `/services` has something to filter on.
 *
 * Three groups rather than six: a visitor arriving at this page is either broken down
 * (Recovery), planning maintenance (Workshop), or asking what else the Foundation can do
 * for them (Together). One group per service would be a filter with six options and one
 * result each, which is a list with extra steps.
 */
export interface GarageService {
  title: Text
  /**
   * Which band this service sits in. A DISCRIMINANT, not copy: `ServicesPage` groups on
   * it and compares it against these literals. `GARAGE_VOCABULARY` carries what each is
   * called on screen.
   */
  group: 'Recovery' | 'Workshop' | 'Together'
  descr: Text
  image: string
  imageAlt: Text
}

export const GARAGE_SERVICES: GarageService[] = [
  {
    title: { en: 'Recovery', mn: 'Чирэх үйлчилгээ' },
    image: '/assets/img/garage-off-road-fleet.jpg',
    imageAlt: { en: 'Vehicles on a forest track at the start of a route', mn: 'Замын эхэнд ойн зам дээрх тээврийн хэрэгслүүд' },
    group: 'Recovery',
    descr:
      { en: 'Vehicle recovery off the trail and off the road, for travellers and for locals — the call you make when the day has gone wrong.', mn: 'Замаас болон замын гадна тээврийн хэрэгсэл чирэх — аялагчид болон нутгийнханд. Өдөр буруугаар эргэсэн үед залгах дугаар.' },
  },
  {
    title: { en: 'Overnight & quick support', mn: 'Шөнийн болон түргэн дэмжлэг' },
    image: '/assets/img/garage-workshop-lift.jpg',
    imageAlt: { en: 'A vehicle raised on a workshop lift, tools and a bench behind it', mn: 'Цехийн өргөгч дээр өргөгдсөн тээврийн хэрэгсэл, ард нь багаж, ажлын ширээ' },
    group: 'Recovery',
    descr:
      { en: 'Fast turnarounds and overnight jobs so a group is not stranded for days: a part fitted, a fault traced, a vehicle back on the route in the morning.', mn: 'Түргэн гүйцэтгэл, шөнийн ажил — бүлэг хэд хоног гацахгүйн тулд: нэг эд анги суулгаж, нэг эвдрэл олж, өглөө нь тээврийн хэрэгсэл замдаа эргэн орно.' },
  },
  {
    title: { en: 'Servicing & maintenance', mn: 'Засвар үйлчилгээ' },
    image: '/assets/img/garage-repair-bench.jpg',
    imageAlt: { en: 'A workshop bench with tools and parts laid out', mn: 'Багаж, эд анги дэлгэсэн цехийн ажлын ширээ' },
    group: 'Workshop',
    descr:
      { en: 'Scheduled servicing, pre-trip checks and maintenance for 4x4s and off-road bikes, done to a standard we are happy to put our name on.', mn: '4x4 болон замын бус мотоциклийн төлөвлөгөөт засвар, аяллын өмнөх шалгалт, арчилгаа — бид нэрээ тавихад баяртай байх түвшинд хийгддэг.' },
  },
  {
    title: { en: 'Off-road tour recovery', mn: 'Замын бус аяллын чирэх үйлчилгээ' },
    image: '/assets/img/garage-riders-group.jpg',
    imageAlt: { en: 'A group of riders and their bikes at the end of a trail day', mn: 'Замын өдрийн төгсгөлд унаачид болон тэдний мотоциклууд' },
    group: 'Recovery',
    descr:
      { en: 'Support arrangements for tour operators and expedition groups, with recovery cover along the routes we know best.', mn: 'Аялал зохион байгуулагчид, экспедицийн бүлгүүдэд зориулсан дэмжлэгийн зохицуулалт — бидний хамгийн сайн мэдэх замуудын дагуух чирэх үйлчилгээний хамт.' },
  },
  {
    title: { en: 'Linked with the resort', mn: 'Амралтын газартай холбоотой' },
    image: '/assets/img/resort-cabins-autumn.jpg',
    imageAlt: { en: 'Cabins at the resort in autumn, a short drive from the workshop', mn: 'Намрын улиралд амралтын газрын байшингууд, цехээс богино зайд' },
    group: 'Together',
    descr:
      { en: 'Break down near us and there is a bed, a meal and a warm room while the work is done — the garage and the resort answer the same phone.', mn: 'Бидний ойролцоо эвдэрвэл ажил хийгдэх хугацаанд ор, хоол, дулаан өрөө байна — гараж, амралтын газар нэг л утсанд хариулдаг.' },
  },
  {
    title: { en: 'Parts & fitting', mn: 'Эд анги, суурилуулалт' },
    image: '/assets/img/carrier360-base.jpg',
    imageAlt: { en: 'A steel frame drilled and finished in the workshop', mn: 'Цехэд өрөмдөж, боловсруулсан ган хүрээ' },
    group: 'Workshop',
    descr:
      { en: 'Sourcing and fitting parts for off-road travel, with honest advice about what a vehicle actually needs before a long stretch.', mn: 'Замын бус аялалд эд анги олж, суурилуулах — урт зам эхлэхийн өмнө тээврийн хэрэгсэлд юу үнэхээр хэрэгтэйг чин сэтгэлээсээ зөвлөнө.' },
  },
]

/**
 * The unpaid side, and the reason the paid side exists. Free to the young people on them.
 */
export const GARAGE_PROGRAMS_HEADING = {
  subTitle: { en: 'Programmes for young people', mn: 'Залуучуудад зориулсан хөтөлбөрүүд' },
  title: { en: 'What A Young Person Can Join', mn: 'Залуу хүн юунд нэгдэж чадах вэ' },
  descr:
    { en: 'Every programme here is free to the young people on it. No fees, no equipment to buy, and no requirement to have done well at school.', mn: 'Энд байгаа хөтөлбөр бүр залуучуудад үнэгүй. Төлбөргүй, худалдан авах тоног төхөөрөмжгүй, сургуульд сайн сурсан байх шаардлагагүй.' },
}

/**
 * `group` is the track a programme belongs to, so `/programmes` can be filtered.
 *
 * "Open to all" is a real group and not a catch-all: the riding days and the safe place
 * are the two things a young person can turn up to with no commitment and no application,
 * which is exactly the distinction a parent or a teenager is looking for on this page.
 */
export interface GarageProgramme {
  title: Text
  /** Which band this programme sits in. A DISCRIMINANT — see `GarageService.group`. */
  group: 'Trade' | 'Technology' | 'Mentoring' | 'Faith' | 'Open to all'
  descr: Text
  image: string
  imageAlt: Text
}

export const GARAGE_PROGRAMS: GarageProgramme[] = [
  {
    title: { en: 'Apprentice mechanic track', mn: 'Дадлагажигч механикчийн чиглэл' },
    image: '/assets/img/garage-workshop-lift.jpg',
    imageAlt: { en: 'A vehicle raised on a workshop lift', mn: 'Цехийн өргөгч дээр өргөгдсөн тээврийн хэрэгсэл' },
    group: 'Trade',
    descr:
      { en: 'Learn the trade on real jobs with real deadlines, beside a mechanic who explains what they are doing and why. Trainees leave with skills, a reference, and often a job.', mn: 'Жинхэнэ хугацаатай жинхэнэ ажил дээр мэргэжлээ сур — юу, яагаад хийж байгаагаа тайлбарладаг механикчийн хажууд. Дадлагажигчид ур чадвар, тодорхойлолт, ихэвчлэн ажилтай гардаг.' },
  },
  {
    title: { en: 'Robotics & technology', mn: 'Робот техник, технологи' },
    image: '/assets/img/garage-repair-bench.jpg',
    imageAlt: { en: 'A workshop bench with tools and parts laid out', mn: 'Багаж, эд анги дэлгэсэн цехийн ажлын ширээ' },
    group: 'Technology',
    descr:
      { en: 'Electronics, diagnostics, coding and robotics for young people who want to know how the modern parts of a vehicle — and everything else — actually work.', mn: 'Тээврийн хэрэгслийн орчин үеийн хэсгүүд — мөн бусад бүхэн — яг яаж ажилладгийг мэдэхийг хүссэн залуучуудад зориулсан электроник, оношилгоо, програмчлал, робот техник.' },
  },
  {
    title: { en: 'Creative arts & fabrication', mn: 'Урлаг, металл боловсруулалт' },
    image: '/assets/img/carrier360-base.jpg',
    imageAlt: { en: 'A steel frame cut, folded and drilled in the workshop', mn: 'Цехэд огтолж, нугалж, өрөмдсөн ган хүрээ' },
    group: 'Trade',
    descr:
      { en: 'Welding, metalwork, paint and design: making things, and finding out you are good at something you had never been given the chance to try.', mn: 'Гагнуур, металл боловсруулалт, будаг, дизайн: юм бүтээж, урьд нь оролдох боломж олгогдоогүй зүйлд чадварлаг болохоо олж мэднэ.' },
  },
  {
    title: { en: 'Life skills & mentoring', mn: 'Амьдралын ур чадвар, зөвлөгөө' },
    image: '/assets/img/garage-riders-group.jpg',
    imageAlt: { en: 'A group gathered together at the end of a ride', mn: 'Аяллын төгсгөлд хамтдаа цугласан бүлэг' },
    group: 'Mentoring',
    descr:
      { en: 'How to work, how to be on time, how to handle money, how to treat people. Each young person is paired with a mentor who stays through the difficult seasons.', mn: 'Яаж ажиллах, яаж цагтаа ирэх, яаж мөнгөө зарцуулах, хүмүүстэй яаж харьцах. Залуу бүрд хүнд үеийг хамт туулах зөвлөх оноодог.' },
  },
  {
    title: { en: 'Riding & trail days', mn: 'Унах, замын өдрүүд' },
    image: '/assets/img/garage-trail-training.jpg',
    imageAlt: { en: 'Two riders in motocross gear on a grass training trail', mn: 'Өвсөн сургалтын зам дээрх мотокроссын хувцастай хоёр унаач' },
    group: 'Open to all',
    descr:
      { en: 'Time on the bikes and out on the trails, where mentoring happens shoulder to shoulder rather than across a desk.', mn: 'Мотоцикл дээрх болон замд гарах цаг — зөвлөгөө ширээний ард биш, мөр зэрэгцэн явагддаг газар.' },
  },
  {
    title: { en: 'A safe place to be', mn: 'Байх аюулгүй газар' },
    image: '/assets/img/garage-riders-camp.jpg',
    imageAlt: { en: 'Riders and their bikes at a camp beside a wooded track', mn: 'Ойн замын хажуугийн зусланд унаачид болон тэдний мотоциклууд' },
    group: 'Open to all',
    descr:
      { en: 'Somewhere to go after school that is warm, welcoming and free of the pressures waiting elsewhere — no programme required to be there.', mn: 'Сургуулийн дараа очих газар — дулаан, тавтай, өөр газар хүлээж буй дарамтгүй. Тэнд байхад хөтөлбөр шаардлагагүй.' },
  },
  {
    title: { en: 'Pathways to work', mn: 'Ажилд хүрэх замууд' },
    image: '/assets/img/carrier360-module-6.jpg',
    imageAlt: { en: 'Tools held upright in a finished carrier module', mn: 'Бэлэн болсон тээгч модульд босоо байрлуулсан багажнууд' },
    group: 'Trade',
    descr:
      { en: 'Trainees leave with practical skills, a reference, and in many cases employment, an apprenticeship or a small business of their own.', mn: 'Дадлагажигчид практик ур чадвар, тодорхойлолт, ихэнх тохиолдолд ажил, дадлагын байр эсвэл өөрийн жижиг бизнестэй гардаг.' },
  },
  {
    title: { en: 'Faith & discipleship', mn: 'Итгэл, шавь бэлтгэл' },
    image: '/assets/img/resort-reading-room.jpg',
    imageAlt: { en: 'A quiet reading room at the resort', mn: 'Амралтын газрын нам гүм уншлагын өрөө' },
    group: 'Faith',
    descr:
      { en: 'Space to ask honest questions, and people who will sit with them — helping young people grow closer to God and recognise the dignity of their own work.', mn: 'Чин сэтгэлийн асуулт асуух орон зай, мөн тэдэнтэй хамт сууж өгөх хүмүүс — залуучуудыг Бурханд ойртож, өөрсдийн хөдөлмөрийн нэр төрийг таньж мэдэхэд тусална.' },
  },
]

/**
 * Testimonies from young people who have come through the workshop.
 *
 * **These are placeholders.** The voices are written to show the shape of the section;
 * they are not real quotations from real trainees. Replace them with recorded testimony
 * — with permission — before this page goes in front of donors.
 */
export interface GarageTestimony {
  quote: Text
  /** What the speaker is called. A placeholder today — see the entries below. */
  name: Text
  role: Text
}

export const GARAGE_TESTIMONIES_HEADING = {
  subTitle: { en: 'In their own words', mn: 'Тэдний өөрсдийнх нь үгээр' },
  title: { en: 'What The Young People Say', mn: 'Залуучууд юу гэж хэлдэг вэ' },
}

export const GARAGE_TESTIMONIES: GarageTestimony[] = [
  {
    quote:
      { en: 'I was told I was not clever because I could not sit still in a classroom. Here somebody put a spanner in my hand and I understood it in an afternoon. I have my own tools now.', mn: 'Ангид тайван суулгүй байсан болохоор надад ухаангүй гэж хэлдэг байсан. Энд хэн нэгэн миний гарт түлхүүр барьуулсан бөгөөд би нэг үдээс хойш л ойлгосон. Одоо би өөрийн багажтай.' },
    name: { en: 'Trainee', mn: 'Дадлагажигч' },
    role: { en: 'Apprentice mechanic — testimony to be recorded', mn: 'Дадлагажигч механикч — гэрчлэлийг бичиж авна' },
  },
  {
    quote:
      { en: 'My mentor kept turning up even the months I did not. That is the part I did not expect, and it is the part that changed things.', mn: 'Миний зөвлөх би ирээгүй сарууд ч байнга ирсээр байсан. Энэ бол миний хүлээгээгүй хэсэг бөгөөд яг энэ л бүхнийг өөрчилсөн.' },
    name: { en: 'Trainee', mn: 'Дадлагажигч' },
    role: { en: 'Second-year trainee — testimony to be recorded', mn: 'Хоёрдугаар жилийн дадлагажигч — гэрчлэлийг бичиж авна' },
  },
  {
    quote:
      { en: 'I came for the bikes. I stayed because for the first time somebody asked me what I wanted to do with my life and then helped me do it.', mn: 'Би мотоциклийн төлөө ирсэн. Харин үлдсэн шалтгаан нь анх удаа хэн нэгэн надаас амьдралдаа юу хийхийг хүсэж байгааг асууж, дараа нь түүнийг хийхэд минь тусалсанд байна.' },
    name: { en: 'Trainee', mn: 'Дадлагажигч' },
    role: { en: 'Now employed locally — testimony to be recorded', mn: 'Одоо орон нутагт ажиллаж байна — гэрчлэлийг бичиж авна' },
  },
]

/**
 * Three invitations, and the point is that the asks are **different in kind**.
 *
 * One costs a young person nothing. One asks a business for time. The third hands the
 * visitor to the resort — because a teenager who needs somewhere to be for a week, or a
 * family who needs a bed while a vehicle is fixed, is not asking the workshop for
 * anything the workshop has.
 *
 * A `Gateway` panel with no destination would be the worst version of this, so it points
 * at what the resort actually offers: **Youth Camps & Retreats, free of charge** — its
 * own words, on `resort.kkfmongolia.com`, in its `WHAT WE GIVE AWAY` section.
 *
 * **If "Gateway" is a named programme with its own meaning, this copy is a placeholder
 * for it.** Nothing in this repository mentions a Gateway before now, so what is written
 * below is the honest reading: the resort as the way through from the workshop. Say what
 * it really is and only these six lines change.
 */
export interface GarageInvitation {
  title: Text
  descr: Text
  list: Text[]
  ctaLabel: Text
  /** An in-app route, or `ctaHref` for a sibling app. Exactly one of the two. */
  ctaTo?: string
  ctaHref?: string
}

export const GARAGE_INVITATIONS: GarageInvitation[] = [
  {
    title: { en: 'Just Come In', mn: 'Зүгээр л орж ирээрэй' },
    descr:
      { en: 'No payment, no signing up, no form. If you are young and curious, come and stand in the workshop and see what happens here. Bring a friend. Stay an hour.', mn: 'Төлбөр ч байхгүй, бүртгүүлэх ч шаардлагагүй, маягт ч байхгүй. Хэрэв та залуу бөгөөд сониуч бол цехэд ирж зогсоод энд юу болж байгааг хараарай. Найзаа дагуулж ирээрэй. Нэг цаг байгаарай.' },
    list: [{ en: 'Nothing to pay, ever', mn: 'Хэзээ ч төлөх зүйлгүй' }, { en: 'No school results needed', mn: 'Сургуулийн дүн шаардлагагүй' }, { en: 'Bring a friend', mn: 'Найзаа дагуулж ирээрэй' }],
    ctaLabel: { en: 'Come And Visit', mn: 'Ирж зочлоорой' },
    ctaTo: '/contact',
  },
  {
    title: { en: 'Gateway', mn: 'Хаалга' },
    descr:
      { en: 'The workshop is one door of four. The resort has the beds, the venues and the library — and it runs youth camps and retreats free of charge, which is where a young person goes when what they need is a week away rather than an afternoon at a bench.', mn: 'Цех бол дөрвөн хаалганы нэг юм. Амралтын газарт ор, танхим, номын сан байдаг — мөн тэнд залуучуудын зуслан, цугларалтыг үнэ төлбөргүй явуулдаг. Залуу хүнд ширээний ард үдээс хойш биш, долоо хоногийн зайлшгүй амралт хэрэгтэй үед тэр очдог.' },
    list: [
      { en: 'Youth camps and retreats, free of charge', mn: 'Залуучуудын зуслан, цугларалт — үнэ төлбөргүй' },
      { en: 'A bed and a meal while a vehicle is fixed', mn: 'Тээврийн хэрэгсэл засагдах хугацаанд ор, хоол' },
      { en: 'Venues, the library, and room to think', mn: 'Танхимууд, номын сан, бодох зай' },
    ],
    ctaLabel: { en: 'Go To The Resort', mn: 'Амралтын газар руу очих' },
    // Leaves the garage entirely — the resort is its own app on its own domain.
    ctaHref: LINKS.resort,
  },
  {
    title: { en: 'Work With Us', mn: 'Бидэнтэй хамтран ажиллах' },
    descr:
      { en: 'Businesses and individuals mentor here, teach a skill for a weekend, take a trainee on placement, or supply the workshop. Partners members do it on member terms.', mn: 'Бизнес, хувь хүмүүс энд зөвлөж, амралтын өдрөөр нэг ур чадвар зааж, дадлагажигч авч, эсвэл цехийг хангадаг. Эвслийн гишүүд үүнийг гишүүний нөхцөлөөр хийдэг.' },
    list: [
      { en: 'Be a mentor in your own trade', mn: 'Өөрийн мэргэжлээр зөвлөх болох' },
      { en: 'Take an apprentice on placement', mn: 'Дадлагажигчийг ажлын байранд авах' },
      { en: '5% member discount across our venues', mn: 'Манай танхимуудад 5% гишүүний хөнгөлөлт' },
      { en: 'A listing in King of Kings Partners', mn: 'King of Kings Partners дахь зар' },
    ],
    ctaLabel: { en: 'Partner With Us', mn: 'Бидэнтэй хамтрах' },
    ctaHref: appUrl(LINKS, 'partners', '/join'),
  },
]

export const GARAGE_ENTERPRISE = {
  subTitle: { en: 'A social enterprise', mn: 'Нийгмийн аж ахуйн нэгж' },
  title: { en: 'Located On One Of Mongolia’s Great Off-Road Routes', mn: 'Монголын нэрт замын бус замуудын нэг дээр байрладаг' },
  paragraphs: [
    { en: 'The garage sits in one of Mongolia’s leading off-road tourism destinations. Vehicles come off those routes needing recovery, repair and maintenance — and that demand turns the workshop into a business, not just a classroom.', mn: 'Гараж нь Монголын замын бус жуулчлалын тэргүүлэх чиглэлүүдийн нэгэнд байрладаг. Тэдгээр замаас гарч ирэх тээврийн хэрэгслүүдэд чирэх, засвар, арчилгаа хэрэгтэй болдог — тэр эрэлт нь цехийг зөвхөн анги биш, бизнес болгодог.' },
    { en: 'Income from the service sustains the ministry and pays for the training, so the young people who need it most are never the ones asked to fund it.', mn: 'Үйлчилгээний орлого номлолыг тэтгэж, сургалтын зардлыг төлдөг тул хамгийн их хэрэгцээтэй залуучуудаас түүнийг санхүүжүүлэхийг хэзээ ч гуйдаггүй.' },
  ],
  ctaLabel: { en: 'Talk To Us About The Garage', mn: 'Гаражийн талаар бидэнтэй ярилцаарай' },
  ctaTo: '/contact',
}

/**
 * The one loud block on the home and services pages: how to get help.
 *
 * **It used to print `123-456-7890` at heading size**, which is the template's placeholder
 * — so the single loudest element on the page was a fake phone number that a stranded
 * customer could actually dial. It is a request button now. That is honest at this stage
 * of the build, and it stays right when the real line arrives: a `tel:` link beside the
 * button is then an addition rather than a correction.
 *
 * The real number is still an open question on the garage PRD.
 */
export const GARAGE_BAND = {
  subTitle: { en: 'Off the road?', mn: 'Замаас гарсан уу?' },
  title: { en: 'Recovery, Repair And Trail Support', mn: 'Чирэх, засвар, замын дэмжлэг' },
  ctaLabel: { en: 'Request Recovery', mn: 'Чирэх үйлчилгээ хүсэх' },
  ctaTo: '/contact',
  note: { en: 'Recovery along the routes we know best. If we cannot reach you, we will tell you who can.', mn: 'Бидний хамгийн сайн мэдэх замуудын дагуух чирэх үйлчилгээ. Хэрэв бид тан дээр очиж чадахгүй бол хэн чадахыг хэлнэ.' },
}

/**
 * What the workshop can actually do, as facts rather than propositions — the question a
 * customer asks before they trust a garage two hundred kilometres from anywhere.
 */
export const GARAGE_SPECS_HEADING = {
  subTitle: { en: 'The workshop', mn: 'Цех' },
  title: { en: 'What We Can Do On Site', mn: 'Бид газар дээр нь юу хийж чадах вэ' },
}

export const GARAGE_SPECS: { term: Text; detail: Text }[] = [
  { term: { en: 'Vehicles', mn: 'Тээврийн хэрэгсэл' }, detail: { en: '4x4s, light trucks, off-road and adventure motorcycles.', mn: '4x4, хөнгөн ачааны машин, замын бус болон адал явдлын мотоцикл.' } },
  { term: { en: 'Recovery', mn: 'Чирэх үйлчилгээ' }, detail: { en: 'Off the trail and off the road, for travellers and for locals.', mn: 'Замаас болон замын гадна — аялагчид болон нутгийнханд.' } },
  { term: { en: 'Turnaround', mn: 'Гүйцэтгэлийн хугацаа' }, detail: { en: 'Overnight and same-day work so a group is not stranded for days.', mn: 'Шөнийн болон тухайн өдрийн ажил — бүлэг хэд хоног гацахгүйн тулд.' } },
  { term: { en: 'Parts', mn: 'Эд анги' }, detail: { en: 'Sourced and fitted, with honest advice on what a vehicle needs before a long stretch.', mn: 'Олж, суурилуулна — урт зам эхлэхийн өмнө тээврийн хэрэгсэлд юу хэрэгтэйг чин сэтгэлээсээ зөвлөнө.' } },
  { term: { en: 'Beds', mn: 'Ор' }, detail: { en: 'The resort is on the same phone: a bed and a meal while the work is done.', mn: 'Амралтын газар нэг л утсанд байна: ажил хийгдэх хугацаанд ор, хоол.' } },
  { term: { en: 'Groups', mn: 'Бүлгүүд' }, detail: { en: 'Recovery cover and support arrangements for tour operators and expedition parties.', mn: 'Аялал зохион байгуулагчид, экспедицийн бүлгүүдэд зориулсан чирэх баталгаа, дэмжлэгийн зохицуулалт.' } },
]

/**
 * The home page's opening.
 *
 * It used to lead with what the place *is* — "A Garage That Builds People As Well As
 * Vehicles" — which is true, is the reason the garage exists, and is not what gets somebody
 * onto a trail. A visitor arriving here is either broken down or looking for a day out, and
 * for the second the first thing on the page has to be the invitation. What the workshop
 * funds is still said, twice, further down; it is no longer the headline.
 */
export const GARAGE_HOME_INTRO = {
  banner: '/assets/img/garage-riders-group.jpg',
  bannerAlt: { en: 'Riders and their bikes gathered under a shade tent at the end of a trail day', mn: 'Замын өдрийн төгсгөлд сүүдрэвчийн дор цугласан унаачид болон тэдний мотоциклууд' },
  subTitle: { en: 'King of Kings Garage · Mongolia', mn: 'King of Kings Garage · Монгол' },
  title: { en: 'Join The Excitement', mn: 'Сэтгэл хөдлөлд нэгдээрэй' },
  descr:
    { en: 'Every kind of off-road adventure, while reconnecting with God and with yourself. Excitement, connection, nature — and memories to last a lifetime.', mn: 'Бурхантай, өөртэйгөө дахин холбогдохын зэрэгцээ бүх төрлийн замын бус адал явдал. Сэтгэл хөдлөл, холбоо, байгаль — мөн насан туршид үлдэх дурсамж.' },
  primaryCta: { label: { en: 'Book A Tour', mn: 'Аялал захиалах' }, to: '/tours' },
  secondaryCta: { label: { en: 'The Workshop', mn: 'Цех' }, to: '/services' },
}

/** The trail promise, in the developer's own words. Opens the home page under the hero. */
export const GARAGE_TRAILS = {
  subTitle: { en: 'Pick your day', mn: 'Өдрөө сонгоорой' },
  title: { en: 'There Is A Trail Out There For Everyone', mn: 'Хүн бүрд тохирох зам бий' },
  lead:
    { en: 'From easy-on-the-axle Jeep roads to hardcore, rugged 4WD trails that bring out your inner adrenaline junkie.', mn: 'Гүүрэнд зөөлөн жийпийн замаас эхлээд доторх адреналин хайгчийг чинь сэрээх хатуу ширүүн 4WD замууд хүртэл.' },
  ctaLabel: { en: 'All Tours & Training', mn: 'Бүх аялал, сургалт' },
  ctaTo: '/tours',
}

/**
 * Where the money goes — the same statement the shop makes, and the reason the tours are
 * not simply a business. Stated on the home page because it is what makes booking here
 * different from booking anywhere else.
 */
export const GARAGE_LOVE = {
  subTitle: { en: 'Love in action', mn: 'Үйлдэл дэх хайр' },
  title: { en: 'Your Business With Us Trains Somebody', mn: 'Танай бидэнтэй хийсэн худалдаа хэн нэгнийг сургадаг' },
  lead:
    { en: 'Doing business with us supports local young people on their journey towards hope and life with Jesus — through membership, skills and training that hold the dignity of all work.', mn: 'Бидэнтэй бизнес хийснээр орон нутгийн залуучуудыг Есүстэй хамт найдвар, амьдрал руу хөтлөх замд нь дэмждэг — аливаа хөдөлмөрийн нэр төрийг хадгалсан гишүүнчлэл, ур чадвар, сургалтаар дамжуулан.' },
  points: [
    { name: { en: 'Youth training', mn: 'Залуучуудын сургалт' }, note: { en: 'A trade taught on real jobs, free to every young person who walks in.', mn: 'Жинхэнэ ажил дээр заадаг мэргэжил — орж ирэх залуу бүрд үнэгүй.' } },
    { name: { en: 'Mentoring', mn: 'Зөвлөгөө' }, note: { en: 'Adults who keep turning up, which is the part that cannot be bought.', mn: 'Байнга ирсээр байдаг насанд хүрэгчид — энэ бол худалдан авч болохгүй хэсэг юм.' } },
    { name: { en: 'A wage on the bench', mn: 'Ажлын ширээн дэх цалин' }, note: { en: 'The gear in our shop is made here, and paid for here.', mn: 'Манай дэлгүүрийн бараа энд хийгддэг, энд төлөгддөг.' } },
  ],
}

/**
 * The contact page's three rows.
 *
 * **The phone number and the email address are still the template's placeholders**, so
 * neither is printed as if it were dialable. `123-456-7890` was being rendered as a `tel:`
 * link, which is the one shape a placeholder must not take on a recovery page — somebody
 * whose vehicle has stopped would tap it. The row says what the channel is for and sends
 * the visitor to the form until the real details arrive.
 *
 * Both are open questions on `docs/01-planning/product-requirements/garage-app-prd.md`.
 * When they land, `value` takes the real detail and `href` becomes the `tel:` or `mailto:`
 * it should always have been.
 */
export const GARAGE_CONTACT_INFOS: {
  icon: string
  title: Text
  /** The inbox is printed as written; everything else in this row is a sentence. */
  value: Text | string
  action: Text
  href?: string
}[] = [
  {
    icon: 'phone-call',
    title: { en: 'Recovery & workshop', mn: 'Чирэх үйлчилгээ, цех' },
    value: { en: 'Direct line to be confirmed — send the form and we will call you back', mn: 'Шууд утсыг тодруулна — маягтаа илгээвэл бид буцаан залгана' },
    action: { en: 'Request recovery', mn: 'Чирэх үйлчилгээ хүсэх' },
    href: '#reach-us',
  },
  {
    // The one contact on this page that is real: the workshop's own inbox, one of the four
    // — `info@`, `garage@`, `resort@`, `partners@` — so a recovery request does not land in
    // the Foundation's general mail.
    icon: 'comment',
    title: { en: 'Email', mn: 'И-мэйл' },
    value: 'garage@kkfmongolia.com',
    action: { en: 'Send an email', mn: 'И-мэйл илгээх' },
    href: 'mailto:garage@kkfmongolia.com',
  },
  { icon: 'location', title: { en: 'Where we are', mn: 'Бид хаана байдаг вэ' }, value: { en: 'Ulaanbaatar, Mongolia', mn: 'Улаанбаатар, Монгол Улс' }, action: { en: 'On the map soon', mn: 'Удахгүй газрын зураг дээр' } },
]

export const GARAGE_CONTACT_HEADING = {
  subTitle: { en: 'Talk to us', mn: 'Бидэнтэй ярилцаарай' },
  title: { en: 'A Recovery, A Placement, Or Just A Look Around', mn: 'Чирэх үйлчилгээ, дадлагын байр, эсвэл зүгээр л нэг эргэж харах' },
  descr:
    { en: 'Three different conversations, one form. Tell us which it is and someone who can answer it will call you back.', mn: 'Гурван өөр яриа, нэг маягт. Аль нь болохыг хэлбэл хариулж чадах хүн тан руу буцаан залгана.' },
  submitLabel: { en: 'Send Message', mn: 'Мессеж илгээх' },
}

/**
 * ── The dark adventure register's own content ──────────────────────────────────────────
 *
 * Slots the pale layout had no place for: the hero's chip and coordinate line, the three
 * figures under it, and the three terrain tiles. Everything else on the rebuilt home page
 * is the copy above, reused where a shape had a slot for it.
 */

/** The hero's chip, its coordinate line, and the three figures under the buttons. */
export const GARAGE_HERO = {
  /**
   * The chip. It names the one time-sensitive thing the workshop offers, because that is
   * what the reference's chip does and it is the only claim here urgent enough to earn
   * the position.
   */
  pillLead: { en: 'Off the road?', mn: 'Замаас гарсан уу?' },
  pillEmphasis: { en: 'Recovery along the route', mn: 'Замын дагуух чирэх үйлчилгээ' },
  /**
   * **Placeholder.** The workshop's real latitude and longitude are not recorded anywhere
   * in this repository, and a plausible-looking coordinate for a recovery service is
   * worse than none — somebody could drive to it. `Ulaanbaatar, Mongolia` is what the
   * contact page already says, so it is what this says until the real fix arrives. Open
   * question on the garage PRD, beside the phone number.
   */
  coords: { en: 'Ulaanbaatar · Mongolia — on one of the great off-road routes', mn: 'Улаанбаатар · Монгол — нэрт замын бус замуудын нэг дээр' },
  /**
   * Three figures, each countable from the data in this repository rather than asserted:
   * `TOURS.length`, `GARAGE_SERVICES.length` and `GARAGE_PROGRAMS.length`. The year is
   * the one fact here that is not derivable, and it is marked.
   */
  stats: [
    { figure: '6', label: { en: 'Tours, training and rentals', mn: 'Аялал, сургалт, түрээс' } },
    { figure: '6', label: { en: 'Workshop services', mn: 'Цехийн үйлчилгээ' } },
    { figure: '8', unit: 'free', label: { en: 'Youth programmes', mn: 'Залуучуудын хөтөлбөр' } },
  ],
}

/**
 * The three terrain tiles.
 *
 * One per audience rather than one per product, which is the split the reference makes
 * too — a visitor knows what they are driving before they know what they want to book.
 * The third is the training side, because a young person or a parent arriving here is
 * the third audience and the pale layout made them scroll past two businesses to find it.
 *
 * **Images: the library has five garage photographs and none is of a 4x4 on a trail.**
 * Each tile carries the closest honest match and the gap is on the PRD.
 */
export const GARAGE_TERRAIN = [
  {
    kicker: { en: 'Terrain / 4x4', mn: 'Газар нутаг / 4x4' },
    title: { en: 'For 4x4 & Overland', mn: '4x4 ба Overland-д' },
    linkLabel: { en: 'See the tours', mn: 'Аялалуудыг үзэх' },
    to: '/tours',
    image: '/assets/img/garage-off-road-fleet.jpg',
    imageAlt: { en: 'A line of vehicles parked along the edge of a forest track', mn: 'Ойн замын захад эгнүүлэн тавьсан тээврийн хэрэгслүүд' },
  },
  {
    kicker: { en: 'Terrain / Moto', mn: 'Газар нутаг / Мото' },
    title: { en: 'For Bikes & ATV', mn: 'Мотоцикл ба ATV-д' },
    linkLabel: { en: 'See the services', mn: 'Үйлчилгээг үзэх' },
    to: '/services',
    image: '/assets/img/garage-trail-training.jpg',
    imageAlt: { en: 'Two riders in motocross gear on a grass training trail', mn: 'Өвсөн сургалтын зам дээрх мотокроссын хувцастай хоёр унаач' },
  },
  {
    kicker: { en: 'Terrain / Floor', mn: 'Газар нутаг / Цех' },
    title: { en: 'For The Training Side', mn: 'Сургалтын талд' },
    linkLabel: { en: 'See the programmes', mn: 'Хөтөлбөрүүдийг үзэх' },
    to: '/programmes',
    image: '/assets/img/garage-workshop-lift.jpg',
    imageAlt: { en: 'A vehicle raised on a workshop lift, tools and a bench behind it', mn: 'Цехийн өргөгч дээр өргөгдсөн тээврийн хэрэгсэл, ард нь багаж, ажлын ширээ' },
  },
]

